import { SpecialSymbolType } from '@/containers/symbols/SpecialSymbolType'
import { SymbolType } from '@/containers/symbols/SymbolType'
import { SoundManager } from '@/managers/sound/SoundManager'
import { CombinationsData, ShowRegularWinningsData, SymbolData, WinCombination } from '@/modules'
import GameSignals from '@/signals/GameSignals'
import { Utilities } from '@base/utils/Utilities'
import { BaseChildState } from '@stateMachine/BaseChildState'
import gsap from 'gsap'

export class RegularWinningsState extends BaseChildState {
    private _fallbackOutWaitTimeoutId: number | null = null
    private _awaitingOutAnimations: boolean = false

    public canTrigger(): boolean {
        return (
            this._model.spinData.winning_combinations &&
            this._model.spinData.winning_combinations[this._model.cascadeIndex].length > 0
        )
    }

    public begin() {
        let hasOutAnimationWinningSymbol = false
        let hasHighPayWinningSymbol = false

        GameSignals.regularWinOutAnimationsComplete.connect(this.onRegularWinOutAnimationsComplete)
        this._awaitingOutAnimations = false

        gsap.delayedCall(0.3, () => {
            let winAmount: number = 0
            for (const combination of this._model.spinData.winning_combinations[this._model.cascadeIndex]) {
                winAmount += combination.win_currency
            }

            const combinations: WinCombination[] = this._model.spinData.winning_combinations[this._model.cascadeIndex]
            hasOutAnimationWinningSymbol = combinations.some((combination: WinCombination) => {
                const symbolId = +combination.symbol_id
                return (
                    symbolId === SymbolType.HP_1 ||
                    symbolId === SymbolType.HP_2 ||
                    symbolId === SymbolType.HP_3 ||
                    symbolId === SymbolType.HP_4 ||
                    symbolId === SymbolType.LP_1 ||
                    symbolId === SymbolType.LP_2 ||
                    symbolId === SymbolType.LP_3 ||
                    symbolId === SymbolType.LP_4 ||
                    symbolId === SymbolType.LP_5
                )
            })
            hasHighPayWinningSymbol = combinations.some((combination: WinCombination) => {
                const symbolId = +combination.symbol_id
                return (
                    symbolId === SymbolType.HP_1 ||
                    symbolId === SymbolType.HP_2 ||
                    symbolId === SymbolType.HP_3 ||
                    symbolId === SymbolType.HP_4
                )
            })

            this._model.winPositions = []
            combinations.forEach((combination: WinCombination) => {
                combination.symbols_positions.forEach((winPosition: number[]) => {
                    if (
                        !this._model.winPositions.some((position: number[]) => {
                            return position[0] === winPosition[0] && position[1] === winPosition[1]
                        })
                    ) {
                        this._model.winPositions.push(winPosition)
                    }
                })
            })

            const data: ShowRegularWinningsData = {
                combinations,
                win: winAmount,
                bet: this._model.currentBaseBet,
                winPositions: this._model.winPositions,
            }

            this.setSoundIndexes(data)

            GameSignals.showRegularWin.emit(data)

            if (hasOutAnimationWinningSymbol) {
                this._awaitingOutAnimations = true
                const fallbackMs = hasHighPayWinningSymbol ? 3200 : 1800
                this._fallbackOutWaitTimeoutId = window.setTimeout(() => {
                    this.onRegularWinOutAnimationsComplete()
                }, fallbackMs)
            } else {
                gsap.delayedCall(1.5, () => {
                    GameSignals.showWinningsComplete.emit()
                    this.end()
                })
            }
        })
    }

    private onRegularWinOutAnimationsComplete = () => {
        if (!this._awaitingOutAnimations) {
            return
        }

        this._awaitingOutAnimations = false

        if (this._fallbackOutWaitTimeoutId !== null) {
            window.clearTimeout(this._fallbackOutWaitTimeoutId)
            this._fallbackOutWaitTimeoutId = null
        }

        GameSignals.showWinningsComplete.emit()
        this.end()
    }

    private setSoundIndexes = (data: ShowRegularWinningsData) => {
        this._model.combinationsData = this.checkCombinations(data)

        if (this._model.combinationsData.isWild) {
            return
        }

        if (!this._model.combinationsData.isWild && this._model.combinationsData.isHP) {
            let newHPIndex: number = Utilities.getRandomInt(0, 2)

            while (SoundManager.hpWinSoundIndex === newHPIndex) {
                newHPIndex = Utilities.getRandomInt(0, 2)
            }

            SoundManager.hpWinSoundIndex = newHPIndex

            return
        }

        if (
            !this._model.combinationsData.isWild &&
            !this._model.combinationsData.isHP &&
            this._model.combinationsData.isLP
        ) {
            let newLPIndex: number = Utilities.getRandomInt(0, 2)

            while (SoundManager.lpWinSoundIndex === newLPIndex) {
                newLPIndex = Utilities.getRandomInt(0, 2)
            }

            SoundManager.lpWinSoundIndex = newLPIndex

            return
        }
    }

    private checkCombinations = (data: ShowRegularWinningsData): CombinationsData => {
        const combinations: WinCombination[] = data.combinations
        let isWild: boolean = false
        let isHP: boolean = false
        let isLP: boolean = false

        for (const combination of combinations) {
            for (const position of combination.symbols_positions) {
                const slotGrid: SymbolData[][] = this._model.spinData.slot_grid[this._model.cascadeIndex]

                if (slotGrid[position[0]][position[1]].id === SpecialSymbolType.MULTIPLIER) {
                    isWild = true
                    break
                }
            }

            if (
                +combination.symbol_id === SymbolType.HP_1 ||
                +combination.symbol_id === SymbolType.HP_2 ||
                +combination.symbol_id === SymbolType.HP_3 ||
                +combination.symbol_id === SymbolType.HP_4
            ) {
                isHP = true
            }

            if (
                +combination.symbol_id === SymbolType.LP_1 ||
                +combination.symbol_id === SymbolType.LP_2 ||
                +combination.symbol_id === SymbolType.LP_3 ||
                +combination.symbol_id === SymbolType.LP_4 ||
                +combination.symbol_id === SymbolType.LP_5
            ) {
                isLP = true
            }
        }

        return {
            isWild,
            isHP,
            isLP,
        }
    }

    public cleanUp() {
        this._awaitingOutAnimations = false
        GameSignals.regularWinOutAnimationsComplete.disconnect(this.onRegularWinOutAnimationsComplete)

        if (this._fallbackOutWaitTimeoutId !== null) {
            window.clearTimeout(this._fallbackOutWaitTimeoutId)
            this._fallbackOutWaitTimeoutId = null
        }
    }
}
